import React, { useContext, useEffect, useMemo, useRef, useState } from 'react'
import { X } from 'lucide-react'
import ApiService from '../../../../services/ApiService'
import UserContext from '../../../../contexts/UserContext'
import {
  CloseButton,
  ErrorText,
  FieldInput,
  FieldLabel,
  FieldSelect,
  FormRow,
  Overlay,
  Panel,
  PanelBody,
  PanelFooter,
  PanelHeader,
  PrimaryButton,
  SecondaryButton,
} from '../ModalStyle'

interface SupplierOption {
  id: number
  companyName: string
}

interface MaterialLookupItem {
  stockId: number | null
  resourceId: number | null
  type: 'MATERIA_PRIMA' | 'INSUMO'
  name: string
  code: string | null
  unitOfMeasurementId: number | null
  unitName: string | null
  unitCost: number | null
  currentStock: number
  minQuantity: number | null
  maxQuantity: number | null
  suppliers: SupplierOption[]
  supplierCompanyId: number | null
  supplierName: string | null
}

interface CreateDemandModalProps {
  onClose: () => void
  onCreated: () => void
}

const formatNumber = (value: number | null | undefined) =>
  value == null ? '-' : Number(value).toLocaleString('pt-BR', { maximumFractionDigits: 4 })

const CreateDemandModal: React.FC<CreateDemandModalProps> = ({ onClose, onCreated }) => {
  const { user } = useContext(UserContext)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const [materialType, setMaterialType] = useState<'MATERIA_PRIMA' | 'INSUMO'>('MATERIA_PRIMA')
  const [materialSearch, setMaterialSearch] = useState('')
  const [materialOptions, setMaterialOptions] = useState<MaterialLookupItem[]>([])
  const [selectedMaterial, setSelectedMaterial] = useState<MaterialLookupItem | null>(null)
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const [loadingMaterials, setLoadingMaterials] = useState(false)
  const searchWrapperRef = useRef<HTMLDivElement>(null)

  const [supplierCompanyId, setSupplierCompanyId] = useState<string>('')

  const openDate = useMemo(
    () =>
      new Date().toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      }),
    [],
  )

  // Busca de materiais no Estoque, filtrada pelo tipo, com debounce.
  useEffect(() => {
    let cancelled = false
    setLoadingMaterials(true)
    const timer = setTimeout(() => {
      ApiService.purchaseMaterial
        .lookup(materialType, materialSearch.trim() || undefined)
        .then((res: any) => {
          if (!cancelled) setMaterialOptions(res.data ?? [])
        })
        .catch(() => {
          if (!cancelled) setMaterialOptions([])
        })
        .finally(() => {
          if (!cancelled) setLoadingMaterials(false)
        })
    }, 300)

    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [materialType, materialSearch])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchWrapperRef.current && !searchWrapperRef.current.contains(event.target as Node)) {
        setDropdownOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleTypeChange = (type: 'MATERIA_PRIMA' | 'INSUMO') => {
    setMaterialType(type)
    setSelectedMaterial(null)
    setMaterialSearch('')
    setSupplierCompanyId('')
  }

  const handleSelectMaterial = (item: MaterialLookupItem) => {
    setSelectedMaterial(item)
    setMaterialSearch(item.name)
    setDropdownOpen(false)

    // Fornecedor: seleção dentre os salvos no item; pré-seleciona o primeiro.
    const suppliers = item.suppliers ?? []
    setSupplierCompanyId(suppliers.length > 0 ? String(suppliers[0].id) : '')
  }

  const supplierOptions = selectedMaterial?.suppliers ?? []

  const isValid = selectedMaterial !== null && supplierCompanyId !== ''

  const handleSubmit = async () => {
    if (!isValid || saving || !selectedMaterial) return
    setSaving(true)
    setError(null)
    try {
      await ApiService.purchaseDemand.create({
        stockId: selectedMaterial.stockId ?? undefined,
        resourceId: selectedMaterial.resourceId ?? undefined,
        materialName: selectedMaterial.name,
        materialType,
        unitOfMeasurementId: selectedMaterial.unitOfMeasurementId ?? undefined,
        supplierCompanyId: Number(supplierCompanyId),
      })
      onCreated()
      onClose()
    } catch (err) {
      console.error('Erro ao criar demanda de compra', err)
      setError('Não foi possível criar a demanda de compra. Verifique os dados e tente novamente.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Overlay onClick={onClose}>
      <Panel $width='34rem' onClick={(e) => e.stopPropagation()}>
        <PanelHeader>
          <h3>Nova Demanda de Compra</h3>
          <CloseButton onClick={onClose}>
            <X size={18} />
          </CloseButton>
        </PanelHeader>
        <PanelBody>
          <div
            style={{
              display: 'flex',
              gap: '1.2rem',
              backgroundColor: '#eaf6fc',
              border: '1px solid #bfe3f5',
              borderRadius: '0.4rem',
              padding: '0.6rem 0.8rem',
              marginBottom: '1rem',
              fontSize: '0.72rem',
            }}
          >
            <span>
              <span style={{ color: '#696969' }}>Aberto em: </span>
              <strong style={{ color: '#0090d3' }}>{openDate}</strong>
            </span>
            <span>
              <span style={{ color: '#696969' }}>Comprador: </span>
              <strong style={{ color: '#0090d3' }}>{user?.name || '-'}</strong>
            </span>
            <span>
              <span style={{ color: '#696969' }}>ID: </span>
              <strong style={{ color: '#0090d3' }}>automático</strong>
            </span>
          </div>
          <FormRow>
            <div>
              <FieldLabel>Tipo*</FieldLabel>
              <FieldSelect
                value={materialType}
                onChange={(e) => handleTypeChange(e.target.value as 'MATERIA_PRIMA' | 'INSUMO')}
              >
                <option value='MATERIA_PRIMA'>Matéria Prima</option>
                <option value='INSUMO'>Insumo</option>
              </FieldSelect>
            </div>
            <div ref={searchWrapperRef} style={{ position: 'relative' }}>
              <FieldLabel>Material*</FieldLabel>
              <FieldInput
                value={materialSearch}
                placeholder={
                  materialType === 'MATERIA_PRIMA'
                    ? 'Buscar matéria-prima no estoque...'
                    : 'Buscar insumo no estoque...'
                }
                onChange={(e) => {
                  setMaterialSearch(e.target.value)
                  setSelectedMaterial(null)
                  setSupplierCompanyId('')
                  setDropdownOpen(true)
                }}
                onFocus={() => setDropdownOpen(true)}
              />
              {dropdownOpen && (
                <div
                  style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    zIndex: 10,
                    backgroundColor: '#fff',
                    border: '1px solid #dcdcdc',
                    borderRadius: '0.4rem',
                    marginTop: '0.2rem',
                    maxHeight: '12rem',
                    overflowY: 'auto',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                  }}
                >
                  {loadingMaterials ? (
                    <div style={{ padding: '0.6rem', fontSize: '0.72rem', color: '#969696' }}>
                      Carregando...
                    </div>
                  ) : materialOptions.length === 0 ? (
                    <div style={{ padding: '0.6rem', fontSize: '0.72rem', color: '#969696' }}>
                      Nenhum material encontrado no estoque.
                    </div>
                  ) : (
                    materialOptions.map((item) => (
                      <div
                        key={`${item.stockId ?? 'r'}-${item.resourceId ?? 's'}`}
                        onClick={() => handleSelectMaterial(item)}
                        style={{
                          padding: '0.45rem 0.6rem',
                          fontSize: '0.74rem',
                          cursor: 'pointer',
                          borderBottom: '1px solid #f4f4f4',
                        }}
                        onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f5fbfe')}
                        onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#fff')}
                      >
                        <div>{item.name}</div>
                        <div style={{ color: '#969696', fontSize: '0.66rem' }}>
                          {item.code ? `Cód. ${item.code}` : 'Sem código'}
                          {item.supplierName ? ` · ${item.supplierName}` : ''}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          </FormRow>
          <FormRow>
            <div>
              <FieldLabel>Unidade</FieldLabel>
              <FieldInput value={selectedMaterial?.unitName ?? '-'} disabled readOnly />
            </div>
            <div>
              <FieldLabel>Fornecedor*</FieldLabel>
              <FieldSelect
                value={supplierCompanyId}
                onChange={(e) => setSupplierCompanyId(e.target.value)}
                disabled={!selectedMaterial}
              >
                <option value=''>
                  {selectedMaterial && supplierOptions.length === 0
                    ? 'Nenhum fornecedor salvo no item'
                    : 'Selecione'}
                </option>
                {supplierOptions.map((supplier) => (
                  <option key={supplier.id} value={supplier.id}>
                    {supplier.companyName}
                  </option>
                ))}
              </FieldSelect>
            </div>
          </FormRow>
          <FormRow>
            <div>
              <FieldLabel>Estoque atual</FieldLabel>
              <FieldInput
                value={selectedMaterial ? formatNumber(selectedMaterial.currentStock) : '-'}
                disabled
                readOnly
              />
            </div>
            <div>
              <FieldLabel>Custo unitário</FieldLabel>
              <FieldInput
                value={selectedMaterial ? formatNumber(selectedMaterial.unitCost) : '-'}
                disabled
                readOnly
              />
            </div>
          </FormRow>
          <FormRow>
            <div>
              <FieldLabel>Estoque mínimo</FieldLabel>
              <FieldInput
                value={selectedMaterial ? formatNumber(selectedMaterial.minQuantity) : '-'}
                disabled
                readOnly
              />
            </div>
            <div>
              <FieldLabel>Estoque máximo</FieldLabel>
              <FieldInput
                value={selectedMaterial ? formatNumber(selectedMaterial.maxQuantity) : '-'}
                disabled
                readOnly
              />
            </div>
          </FormRow>
          {error && <ErrorText>{error}</ErrorText>}
        </PanelBody>
        <PanelFooter>
          <SecondaryButton onClick={onClose}>Cancelar</SecondaryButton>
          <PrimaryButton disabled={!isValid || saving} onClick={handleSubmit}>
            {saving ? 'Salvando...' : 'Criar demanda'}
          </PrimaryButton>
        </PanelFooter>
      </Panel>
    </Overlay>
  )
}

export default CreateDemandModal
